/**
 * Renders the conditional modals for the Documents page and
 * synchronises the imperative `openModal` calls with the Redux `openedModalId`.
 * @module DocumentModals
 */

import { useEffect } from "react";
import { openModal } from "risa-oasis-ui_v2";
import DocumentScreenshotViewerModal from "../../../../../components/modals/documentScreenshotViewerModal/documentScreenshotViewerModal";
import DocViewerModalwithTitle from "../../../../../components/modals/docViewerModalwithTitle/docViewerModalwithTitle";
import NarLetterModal from "../../../../../components/modals/narLetterModal/narLetterModal";
import TriggerRpaWithDocListModal from "../../../../../components/modals/triggerRpaWithDocListModal/triggerRpaWithDocListModal";
import {
  AuthOnFile,
  JCodeDocuments,
} from "../../../../../data-model/medicalPaOrdersModel";
import { ModalId } from "../../../../../enums/modalId";
import { closeModal } from "../../../../../redux/slice/modalSlice";
import { AppDispatch } from "../../../../../redux/store/store";
import { getAllUniqueDocsFromJCodesInFlattenedArray } from "../../../../singleOrderLayout/utils/docsForRpa";

/**
 * Props for {@link DocumentModals}: modal id from Redux, order context,
 * RPA save handler, loading flag, auth-on-file payload, and dispatch.
 */
interface DocumentModalsProps {
  openedModalId: string | null;
  orderId: string;
  handleSaveChanges: (
    docs: JCodeDocuments[],
    selectedOrderIds?: string[],
  ) => Promise<void>;
  loading: boolean;
  unwrappedAuthOnFileDetails: AuthOnFile | null;
  dispatch: AppDispatch;
}

/**
 * Manages modal-open side-effects and renders the four document-page modals
 * (`NarLetterModal`, `DocViewerModalwithTitle`, `TriggerRpaWithDocListModal`,
 * `DocumentScreenshotViewerModal`) based on `openedModalId`.
 *
 * @param props - Modal state, order id, RPA handler, and dispatch.
 */
const DocumentModals = ({
  openedModalId,
  orderId,
  handleSaveChanges,
  loading,
  unwrappedAuthOnFileDetails,
  dispatch,
}: DocumentModalsProps) => {
  useEffect(() => {
    if (openedModalId === ModalId.TRIGGER_RPA_WITH_DOC_LIST_MODAL) {
      openModal(ModalId.TRIGGER_RPA_WITH_DOC_LIST_MODAL);
    }
    if (openedModalId === ModalId.UPDATE_CIG_NOTE_CONFIRMATION_DIALOG) {
      openModal(ModalId.UPDATE_CIG_NOTE_CONFIRMATION_DIALOG);
    }
    if (openedModalId === ModalId.DOCUMENT_SCREENSHOT_VIEWER_MODAL) {
      openModal(ModalId.DOCUMENT_SCREENSHOT_VIEWER_MODAL);
    }
  }, [openedModalId]);

  return (
    <>
      {openedModalId === ModalId.NAR_LETTER_MODAL && (
        <NarLetterModal
          onClose={() => dispatch(closeModal())}
          showDragDropUpload={true}
          shouldLogEvent={true}
        />
      )}
      {openedModalId === ModalId.DOC_VIEWER_MODAL_WITH_TITLE && (
        <DocViewerModalwithTitle onClose={() => dispatch(closeModal())} />
      )}
      {openedModalId === ModalId.TRIGGER_RPA_WITH_DOC_LIST_MODAL && (
        <TriggerRpaWithDocListModal
          onTriggerRpa={handleSaveChanges}
          onClose={() => dispatch(closeModal())}
          onCancel={() => dispatch(closeModal())}
          loading={loading}
          orderId={orderId}
          docList={getAllUniqueDocsFromJCodesInFlattenedArray(
            unwrappedAuthOnFileDetails as AuthOnFile,
          )}
        />
      )}
      {openedModalId === ModalId.DOCUMENT_SCREENSHOT_VIEWER_MODAL && (
        <DocumentScreenshotViewerModal />
      )}
    </>
  );
};

export default DocumentModals;
